import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaBook, FaMoneyBillWave, FaComments, FaChartLine } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

import AdminLayout from '../../components/layout/AdminLayout';
import ManageCourses from './ManageCourses';
import StudentList from './StudentList';
import PaymentVerification from './PaymentVerification';
import SecuritySettings from './SecuritySettings';
import ActivityLog from './ActivityLog';
import AdminFeedback from './AdminFeedback';
import ManageSchedules from './ManageSchedules';
import AdminGallery from './AdminGallery';
import AdminResources from './AdminResources';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/get_stats.php`);
      const data = await response.json();
      if (data.status === 'success') {
        setStats(data.stats);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'Manage Courses': return <ManageCourses />;
      case 'Students': return <StudentList />;
      case 'Payments': return <PaymentVerification />;
      case 'Schedules': return <ManageSchedules />;
      case 'Feedback': return <AdminFeedback />;
      case 'Gallery': return <AdminGallery />;
      case 'Resources': return <AdminResources />;
      case 'Security Settings': return <SecuritySettings />;
      case 'System History': return <ActivityLog />;
      case 'Overview':
      default:
        return (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {loading ? (
                [1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-50 rounded-2xl animate-pulse border border-slate-100"></div>)
              ) : (
                stats.map((stat, i) => (
                  <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 ${stat.color} bg-opacity-10 rounded-xl flex items-center justify-center text-xl`} style={{ color: stat.color.replace('bg-', 'text-') }}>
                        {renderIcon(stat.icon)}
                      </div>
                    </div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                  </div>
                ))
              )}
            </div>

            {/* Quick Actions & Health */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-3">
                  <span className="w-2 h-6 bg-orange-500 rounded-full"></span>
                  Quick Actions
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <QuickAction title="Add Course" onClick={() => setActiveTab('Manage Courses')} color="orange" />
                  <QuickAction title="View Students" onClick={() => setActiveTab('Students')} color="blue" />
                  <QuickAction title="Verify Payments" onClick={() => setActiveTab('Payments')} color="teal" />
                  <QuickAction title="Reports" onClick={() => {}} color="red" />
                </div>
              </div>

              <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-3">
                  <span className="w-2 h-6 bg-teal-500 rounded-full"></span>
                  System Health
                </h2>
                <div className="space-y-4">
                   <HealthItem label="API Status" status="Online" color="green" />
                   <HealthItem label="Database" status="Connected" color="green" />
                   <HealthItem label="Last Backup" status="Today, 04:00 AM" color="blue" />
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'FaUsers': return <FaUsers />;
      case 'FaBook': return <FaBook />;
      case 'FaMoneyBillWave': return <FaMoneyBillWave />;
      case 'FaComments': return <FaComments />;
      default: return <FaChartLine />;
    }
  };

  return (
    <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab} title={activeTab}>
      {renderContent()}
    </AdminLayout>
  );
};

const QuickAction = ({ title, onClick, color }) => {
  const colors = {
    orange: "bg-white text-orange-500 hover:bg-orange-500 hover:text-white border border-orange-100 shadow-sm",
    blue: "bg-white text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-100 shadow-sm",
    teal: "bg-white text-teal-500 hover:bg-teal-500 hover:text-white border border-teal-100 shadow-sm",
    red: "bg-white text-red-500 hover:bg-red-500 hover:text-white border border-red-100 shadow-sm",
  };
  return (
    <button onClick={onClick} className={`${colors[color]} p-4 rounded-2xl font-bold text-sm transition-all active:scale-95 text-center`}>
      {title}
    </button>
  );
};

const HealthItem = ({ label, status, color }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-white shadow-sm">
    <span className="text-sm font-medium text-slate-500">{label}</span>
    <span className={`text-[10px] font-bold text-${color}-600 bg-${color}-50 px-2 py-0.5 rounded uppercase tracking-widest`}>{status}</span>
  </div>
);

export default AdminDashboard;
